import { Controller } from "@hotwired/stimulus";

/**
 * Un champ de texte dont la syntaxe se colore : les variables (`$NOM`, avec leurs chaînes
 * `.upcase`, `[0..2]`…) et la logique (`?{(…) …}`, `ALEA{…|…}`). Le texte de l'élément
 * éditable est transparent ; un miroir coloré (`visible`) se superpose, resynchronisé à
 * chaque frappe.
 *
 * Deux éditables possibles pour `hidden` : un `<textarea>` (le panneau Variables de la
 * toolbox — sa valeur est la source, le miroir fait la hauteur du bloc), ou un
 * `contenteditable` (les body_configs de collection — le texte est recopié dans un champ
 * caché, `formField`, par `decodeHTML`).
 *
 * Les couleurs sont des classes passées par la vue (`data-highlighted-input-variable-class`,
 * `-logic-class`) : Tailwind ne compile que les noms qu'il lit en clair, dans la vue ou ici.
 *
 * Naming: "highlighted-input" in HTML.
 */
export default class extends Controller {
  static targets = ["hidden", "visible", "formField"];
  static classes = ["variable", "logic"];

  connect() {
    this.synchronize();
  }

  synchronize() {
    // Le caractère de largeur nulle garde la hauteur d'une dernière ligne vide.
    this.visibleTarget.innerHTML = `${this.highlight(this.source)}​`;
  }

  // Le contenteditable vers le champ de formulaire : ses <div>/<br> redeviennent des lignes.
  decodeHTML() {
    const content = this.hiddenTarget.innerHTML
      .replace(/<div>/g, "\n")
      .replace(/<br>/g, "\n")
      .replace(/<[^>]*>?/gm, "")
      .replace("\n\n\n", "\n\n");
    const decoder = document.createElement("textarea");
    decoder.innerHTML = content;
    this.formFieldTarget.value = decoder.value;
    this.formFieldTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  scrollMirror() {
    this.visibleTarget.scrollTo(this.hiddenTarget.scrollLeft, this.hiddenTarget.scrollTop);
  }

  // Le texte à colorer, déjà échappé : le navigateur l'a fait pour un contenteditable, on le
  // fait pour un textarea.
  get source() {
    const el = this.hiddenTarget;
    if (el instanceof HTMLTextAreaElement) {
      return el.value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }
    return el.innerHTML;
  }

  highlight(text) {
    const wrapLogic = (part) => `<span class="${this.logicClassName}">${part}</span>`;
    const wrapVariable = (part) => `<span class="${this.variableClassName}">${part}</span>`;

    const parse = (input) => {
      const processed = input.replace(/ALEA\{([\s\S]*?)\}/g, (_match, content) => {
        return wrapLogic("ALEA{") + content.replace(/\|/g, wrapLogic("|")) + wrapLogic("}");
      });

      let result = "";
      let i = 0;
      while (i < processed.length) {
        if (!processed.substring(i).startsWith("?{(")) {
          result += processed[i];
          i++;
          continue;
        }

        // La condition : jusqu'à la parenthèse fermante équilibrée
        const conditionStart = i + 3;
        let parenLevel = 1;
        let conditionEnd = conditionStart;
        while (parenLevel > 0 && conditionEnd < processed.length) {
          if (processed[conditionEnd] === "(") parenLevel++;
          if (processed[conditionEnd] === ")") parenLevel--;
          conditionEnd++;
        }
        if (parenLevel !== 0) {
          result += processed.substring(i);
          break;
        }
        const condition = processed.substring(conditionStart, conditionEnd - 1);

        // Le corps : jusqu'à l'accolade fermante équilibrée (expressions imbriquées comprises)
        const bodyStart = conditionEnd;
        let braceLevel = 1;
        let expressionEnd = bodyStart;
        while (braceLevel > 0 && expressionEnd < processed.length) {
          if (processed.substring(expressionEnd).startsWith("?{(")) {
            braceLevel++;
            expressionEnd += 3;
          } else {
            if (processed[expressionEnd] === "{") braceLevel++;
            if (processed[expressionEnd] === "}") braceLevel--;
            expressionEnd++;
          }
        }
        if (braceLevel !== 0) {
          result += processed.substring(i);
          break;
        }
        const body = processed.substring(bodyStart, expressionEnd - 1);
        result += wrapLogic(`?{(${condition})`) + parse(body) + wrapLogic("}");
        i = expressionEnd;
      }

      return result.replace(
        /\$(\w+)((?:\.\w+(?:\("[^"]*"\))?|\[-?\d+(?:\.\.-?\d+|\.\.)?\]|\[\.\.(?:-?\d+)\])*)/g,
        (match) => wrapVariable(match),
      );
    };

    return parse(text);
  }

  // `xClass` ne rend que la première classe de l'attribut : on prend la liste entière.
  get variableClassName() {
    return this.hasVariableClass ? this.variableClasses.join(" ") : "text-emerald-400";
  }

  get logicClassName() {
    return this.hasLogicClass ? this.logicClasses.join(" ") : "text-blue-400";
  }
}
